import { COUNTRY_ID_BY_CODE } from "Utilities/Address/networkCountryOptions";

/**
 * Merge a delta emitted by {@link useAddressAutocomplete} into a surface's
 * persisted address value. The inverse boundary of {@link toHookValue}: the
 * hook speaks ISO country codes, so a `delta.country` is an ISO string.
 *
 * The merged value keeps the ISO string in `country` and writes the numeric
 * ID to `countryID`. That is the canonical persisted shape: the backend
 * (`Services/ExtendedField/ResponseAssembler/AddressResponseAssembler.php`)
 * accepts a numeric ID or an ISO code in either key and normalizes to the ISO
 * code for storage, so stored values round-trip with the ISO string in
 * `country` — and ID-keyed consumers (the country dropdown, `useAddressDirty`)
 * read `countryID`.
 *
 * @param {Record<string, unknown>} base - the surface's current address value.
 * @param {Record<string, unknown>} delta - the hook's change delta.
 * @param {{ markDirty?: boolean }} [opts] - `markDirty` stamps
 *   `_addressIsDirty: "1"`, the client-side signal some save owners gate on
 *   (stripped before storage; the backend never reads it).
 * @returns {Record<string, unknown>} the merged address value.
 */
export default function mergeHookDelta(base, delta, { markDirty = false } = {}) {
  const next = { ...base, ...delta };
  if (typeof delta.country === "string") {
    const id = COUNTRY_ID_BY_CODE[delta.country];
    if (id) next.countryID = id;
  }
  if (markDirty) next._addressIsDirty = "1";
  return next;
}
